import React from 'react';

import { useIntl } from 'react-intl';

import { GET_ENTITY_RESOURCE } from 'src/features/entity/api/useGetEntity';
import { useUpdateEntity } from 'src/features/entity/api/useUpdateEntity';
import EntityFormModal from 'src/features/entity/components/forms/EntityFormModal';
import { Entity, ENTITY_TYPE } from 'src/features/entity/types';
import { entityMessages } from 'src/features/entity/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface EntityFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedEntity: Entity;
    children?: React.ReactNode;
}

function UpdateEntityFormModal<TData>({ className, selectedEntity, children }: EntityFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const entityType = selectedEntity.type === ENTITY_TYPE.PERSON ? entityMessages.entity : entityMessages.group;
    const updatedSuccessDescription =
        selectedEntity.type === ENTITY_TYPE.PERSON
            ? entityMessages.updatePersonSuccessDescription
            : entityMessages.updateGroupSuccessDescription;
    const { parkingId } = useParking();

    const { mutate: updateEntity, isPending } = useUpdateEntity({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [GET_ENTITY_RESOURCE] });
            toast({
                title: formatMessage(entityMessages.updateEntitySuccess),
                description: formatMessage(updatedSuccessDescription),
            });
        },
    });

    return (
        <EntityFormModal
            className={className}
            selectedEntity={selectedEntity}
            mutate={(values, onSuccess, onError) =>
                updateEntity(
                    { ...values, type: selectedEntity.type, id: selectedEntity.id, parkingId },
                    { onSuccess, onError },
                )
            }
            type={selectedEntity.type}
            title={formatMessage(generalMessages.editEntry, {
                entry: formatMessage(entityType).toLowerCase(),
            })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(entityMessages.editEntitySubtitle, { entry: selectedEntity.name })}
        >
            {children}
        </EntityFormModal>
    );
}

export default UpdateEntityFormModal;
